import {compose, DispatchableAsync, Dispatcher, DispatcherActionLike} from "../utils"
import {connect, Store} from "react-redux";
import {Axes, DisplayModes} from "./graph-renderer";
import {Report} from "./lib1";
import {FlatReportChartData, ReportChartRow} from "./chart-data"
import {Filters, FilterState, ReportPropsCallback, ReportTypes, ReportValueProps} from "./lib2";
import {Action} from "redux";

type FilterType="check box"|"select box"|"date range"

export type ReportFilterAction<FF extends Filters> =
	| SetFilterAction<FF>
	| UnsetCheckboxFilterAction<FF>
	| SetCheckboxFilterAction<FF>

export type UnsetCheckboxFilterAction<FF extends Filters> = {type: "unset filter checkbox", filter: keyof FF, value: string}
export type SetCheckboxFilterAction<FF extends Filters> = {type: "set filter checkbox", filter: keyof FF, value: string}
export type SetFilterAction<FF extends Filters> = {type: "set filter", filter: keyof FF, filterData: Partial<FilterState> & {type: FilterType} }

export type ReportAction<AA extends Axes, FF extends Filters, DD extends DisplayModes, RR extends ReportTypes<AA, FF, DD>> =
	{type: "set report type", value: keyof RR}
	| {type: "set new report data", data: ReportStateData<AA>}
	| {type: "set display mode", mode: keyof DD}
	| ReportFilterAction<FF>

export type ReportActionWrapper<AA extends Axes, FF extends Filters, DD extends DisplayModes, RR extends ReportTypes<AA, FF, DD>> = {
	type: "report action"
	action: ReportAction<AA, FF, DD, RR>
}

/**
todo: The definition for the pie chart data should probably be referencing
todo: a ReportChartRow though the indexes here are numbers rather than strings.
todo: Additionally, if the pie chart data did reference a
todo: ReportChartRow, then this whole thing would become very similar
todo: to the type definition => export type ReportData = MultiReportData | InstantReportData (in chart-data.ts)
todo: and could probably be refactored into:
todo:
todo:        type ReportStateData<AA extends Axes> = ReportData & {axis: keyof AA}
*/

type ReportStateData<AA extends Axes> =
	{data: ReportChartRow, renderAs: 'pie', axis: keyof AA}
	| {data: FlatReportChartData, renderAs: 'bar'|'line', axis: keyof AA}

export type ReportState<AA extends Axes, FF extends Filters, DD extends DisplayModes, RR extends ReportTypes<AA, FF, DD>> = {
	reportType: keyof RR
	displayMode: keyof DD
	filters: {[P in keyof FF]: FilterState}
	reportData?: ReportStateData<AA>
}

export type ReportOwnProps<AA extends Axes, FF extends Filters, DD extends DisplayModes, RR extends ReportTypes<AA, FF, DD>, State> = {
	reportTypes: RR,
	filters: FF,
	axes: AA
	runReport: (d: DispatcherActionLike<ReportAction<AA, FF, DD, RR>, Action>) => DispatchableAsync<Action, State, void>
	title: string
	modes: DD
}

export type ReportStoreOwnProps<AA extends Axes, FF extends Filters, DD extends DisplayModes, RR extends ReportTypes<AA, FF, DD>, State> = {
	store: Store<ReportState<AA, FF, DD, RR>>,
} & ReportOwnProps<AA, FF, DD, RR, State>

export const mapDispatchToProps = <
	AA extends Axes,
	FF extends Filters,
	DD extends DisplayModes,
	RR extends ReportTypes<AA, FF, DD>,
	State
>(
	dispatch_: Dispatcher<Action, State>,
	ownProps: ReportOwnProps<AA, FF, DD, RR, State>
): ReportPropsCallback<keyof RR, keyof FF, DD> => {
	const wrap = (action: ReportAction<AA, FF, DD, RR>): ReportActionWrapper<AA, FF, DD, RR> => ({
		type: "report action",
		action
	})
	const dispatchAction = compose<ReportAction<AA, FF, DD, RR>, Action, Action>(wrap, dispatch_)
	return ({
		setDisplay: (mode) => dispatchAction({type: "set display mode", mode}),
		runReport: () => dispatch_(ownProps.runReport(dispatchAction)),
		onSetReportType: (reportType: keyof RR) => dispatchAction({type: "set report type", value: reportType}),
		filterCallbacks: {
			onSetCheckbox: (filter, value) => dispatchAction({type: "set filter checkbox", filter, value}),
			onUnsetCheckbox: (filter, value) => dispatchAction({type: "unset filter checkbox", filter, value}),
			onSelect: (s, f) => dispatchAction({
				type: "set filter",
				filter: f,
				filterData: {type: "select box", selected: s}
			}),
			onSetFromDate: (d: Date, f: keyof FF) => dispatchAction({
				type: "set filter",
				filter: f,
				filterData: {type: "date range", fromDate: d}
			}),
			onSetToDate: (d: Date, f: keyof FF) => dispatchAction({
				type: "set filter",
				filter: f,
				filterData: {type: "date range", toDate: d}
			}),
		}
	})
}

export const mapStateToProps = <
	AA extends Axes,
	FF extends Filters,
	DD extends DisplayModes,
	RR extends ReportTypes<AA, FF, DD>,
	State
>(state: ReportState<AA, FF, DD, RR>, ownProps: ReportOwnProps<AA, FF, DD, RR, State>):
	ReportValueProps<AA, FF, DD, RR> => ({
	title: ownProps.title,
	displayMode: state.displayMode,
	displayModes: ownProps.modes,
	chartData: state.reportData,
	reportType: state.reportType,
	reportTypes: ownProps.reportTypes,
	filters: ownProps.filters,
	filterState: state.filters,
	axes: ownProps.axes
})

export const makeReportApp = <AA extends Axes, FF extends Filters, DD extends DisplayModes, RR extends ReportTypes<AA, FF, DD>, State>() =>
	connect<
		ReportValueProps<AA, FF, DD, RR>,
		ReportPropsCallback<keyof RR, keyof FF, DD>,
		ReportStoreOwnProps<AA, FF, DD, RR, State>,
		ReportState<AA, FF, DD, RR>
	>(
		(reportState, ownProps) => mapStateToProps<AA, FF, DD, RR, State>(reportState, ownProps),
		(dispatcher, ownProps) => mapDispatchToProps<AA, FF, DD, RR, State>(dispatcher, ownProps)
	)(Report)



// WEBPACK FOOTER //
// ./src/lib/report-builder/lib.ts